<template lang="pug">
    .product-export(v-loading="categories.loading")
        //- (v-if="this.actions.initialize === null")
        .alert.info(v-if="info")
            p {{ info }}
        .alert.danger(v-if="error")
            p {{ error }}
            a(href="#" @click.prevent.stop="initialize") {{ $t('Try again') }}
        .form(v-else)
            .export-info-panel(v-if="currentExport")
                .fresh(v-if="currentExport.status === 'fresh'")
                    .alert.primary
                        p {{ $t('Your export has been queued and will start as soon as possible.') }}
                        p {{ $t('Please check later') }},&nbsp
                            a(href="#" @click.stop.prevent="initialize") {{ $t('or refresh now') }}
                .pending(v-if="currentExport.status === 'pending'")
                    .alert.primary
                        p {{ $t('An export for this category is in the queue and will start as soon as possible.') }}
                        p {{ $t('Please check later') }},&nbsp
                            a(href="#" @click.stop.prevent="initialize") {{ $t('or refresh now') }}
                .running(v-if="currentExport.status === 'running'")
                    // .percentage
                    //     el-progress(:stroke-width="6" :percentage="Math.round(currentExport.percentage * 10) / 10")
                    .alert.primary
                        .percentage
                            el-progress(
                                type="dashboard"
                                :width="100"
                                :percentage="currentExport.percentage"
                                :format="percentage => `${Math.round(percentage * 100) / 100}%`"
                            )
                        .info(v-if="currentExport.percentage < 100")
                            p {{ $t('An export for this category is currently running.') }}
                            // p {{ $t('It has been started {time} ago and is currently at {percentage}%.', { time: formatDistanceToNow(currentExport.started_at), percentage: Math.round(currentExport.percentage * 100) / 100 }) }}
                            p {{ $t('Started: {time}', { time: formatDistanceToNow(currentExport.started_at), percentage: Math.round(currentExport.percentage * 100) / 100 }) }}
                            p(v-if="estimatedCompletion") {{ $t('Estimated completion: {time}.', { time: estimatedCompletion }) }}
                            p {{ $t('Please check later') }},&nbsp
                                a(href="#" @click.stop.prevent="initialize") {{ $t('or refresh now') }}
                        .info(v-else)
                            p {{ $t('The export has been completed.') }}
                .failed(v-if="currentExport.status === 'failed'")
                    .alert.danger
                        p {{ $t('The last export could not be completed successfull. It failed {time} ago.', { time: formatDistanceToNow(currentExport.finished_at) }) }}
                .finished(v-if="currentExport.status === 'finished'")
                    .alert.success
                        p {{ $t('The Last export has been finished successfully {time} ago.', { time: formatDistanceToNow(currentExport.finished_at) }) }}

            el-form.dynamic-form(v-if="!unsaved && (!currentExport || currentExport.finished_at !== null)")
                template(v-for="field in additionalFieldConfigs")
                    InputField(:config="field" :form="form" :key="field.key")
                .inline-form-fields
                    el-form-item.buttons.no-margin
                        el-button.start-export(
                            type="primary"
                            /* :disabled="!form.source" */
                            @click="start"
                        )
                            | {{ $t('Export') }}
</template>

<i18n>
{
    "de": {
        "Technical issue! Product export could not be initialized.": "Technisches Problem! Produktexport konnte nicht initialisiert werden.",
        "Product export could not be started.": "Produktexport konnte nicht gestartet werden.",
        "Product export could not be stopped.": "Produktexport konnte nicht gestoppt werden.",
        "This category has not yet been saved. You need to save it in order to export products.": "Diese Kategorie ist noch nicht gespeichert. Sie müssen sie speichern, um Produkte exportieren zu können.",
        "An export for this category is in the queue and will start as soon as possible.": "Aktuell befindet sich für diese Kategorie bereits ein Export in der Warteschlange und wird so schnell wie möglich gestartet.",
        "An export for this category is currently running.": "Aktuell befindet sich für diese Kategorie bereits ein Export in der Verarbeitung.",
        "It has been started {time} ago and is currently at {percentage}%.": "Der Export wurde vor {time} gestartet und ist aktuell bei {percentage}%.",
        "Started: {time}": "Gestartet vor: {time}",
        "The Last export has been finished successfully {time} ago.": "Der letzte export wurde erfolgreich abgeschlossen vor {time}.",
        "The last export could not be completed successfull. It failed {time} ago.": "Der letzte Export konnte nicht erfolgreich abgeschlossen werden. Fehlgeschlagen vor {time}.",
        "Estimated completion: {time}.": "Voraussichtliche Dauer bis zur Fertigstellung: {time}",
        "Your export has been queued and will start as soon as possible.": "Dein Export wurde erfolgreich erfasst und wird so schnell wie möglich gestartet.",
        "Please check later": "Schau doch später nochmal vorbei",
        "The export has been completed.": "Der Export wurde erfolgreich abgeschlossen.",
        "Source category": "Quellkategorie",
        "or refresh now": "oder aktualisiere jetzt",
        "With stock": "Mit Lagerbestand",
        "Without stock": "Ohne Lagerbestand",
        "Articles": "Artikel",
        "Select source...": "Quelle wählen...",
        "Create sub-categories": "Unterkategorien erstellen",
        "Export": "Exportieren",
        "Try again": "Erneut versuchen"
    },
    "en": {
        "It has been started {time} ago and is currently at {percentage}%.": "It has been started {time} ago and is currently at {percentage}%.",
        "Started: {time}": "Started: {time}",
        "Estimated completion: {time}.": "Estimated completion: {time}.",
        "The last export could not be completed successfull. It failed {time} ago.": "The last export could not be completed successfull. It failed {time} ago.",
        "The Last export has been finished successfully {time} ago.": "The Last export has been finished successfully {time} ago."
    }
}
</i18n>

<script>
import _ from 'lodash';
import { formatDistanceToNow, fromUnixTime, getUnixTime } from 'date-fns';
import { activeDateLocale } from '@/utils/dateLocale';

import { adminClient as apolloClient } from '@/store/apollo';
import pinia from '@/pinia';
import { useSetupStore, useShopCategoriesStore } from '@/stores';

import ProductExportApi from '@/extensions/shop/store/api/productExport';
import InputField from './ProductTransferInputField';

import Treeselect from '@riophae/vue-treeselect';

export default {
    name: 'ProductExport',
    props: {
        config: {
            type: Object,
            required: true,
        },
        category: {
            type: Object,
            required: true,
        }
    },
    data() {
        const source = {
            id: this.category.id,
            key: this.category.key,
            name: this.category.name,
        }
        return {
            info: false,
            error: false,
            actions: {
                initialize: undefined,
                start: undefined,
                stop: undefined,
            },
            categories: {
                items: [],
                loading: false,
            },
            currentExport: undefined,
            form: {
                source,
            }
        }
    },
    components: {
        InputField,
        Treeselect,
    },
    computed: {
        unsaved() {
            return !this.category.id || _.startsWith(this.category.id, 'new:')
        },
        estimatedCompletion() {
            if (!this.currentExport || this.currentExport.percentage <= 0) return false;

            const secondsSinceStarted = getUnixTime(new Date) - this.currentExport.started_at;
            const estimatedDuration = parseInt(secondsSinceStarted / this.currentExport.percentage * 100);

            return this.formatDistanceToNow(estimatedDuration + parseInt(this.currentExport.started_at));
        },
        additionalFieldConfigs() {
            const fields = _.get(this.config, 'fields') || []
            // .filter(el => ['manage-stock', 'create-sub-categories'].includes(el.key) === false)
            return fields;
        },
        setupStore() {
            return useSetupStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        settingValues() {
            return this.setupStore.settingValues;
        },
        categoriesStore() {
            return useShopCategoriesStore(pinia);
        },
    },
    methods: {
        formatDistanceToNow(timestamp) {
            return formatDistanceToNow(fromUnixTime(parseInt(timestamp)), { locale: activeDateLocale() })
        },
        initialize() {
            if (!this.actions.initialize) {
                this.error = this.$t('Technical issue! Product export could not be initialized.')
                return;
            }

            const settings = this.settingValues;

            let api = this.actions.initialize.api;
            let url = this.actions.initialize.url;
            let token = this.actions.initialize.token;

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            let method, config;
            switch (api.toLowerCase()) {
                case 'rest':
                    if (token) {
                        config = {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        };
                    }
                    method = ProductExportApi.REST;
                    break;

                // TODO
                // case 'graphql':
                //     method = MicroserviceApi.REST
                //     break;
            }

            // fetch categories
            if (!method) {
                this.error = this.$t('Technical issue! Product export could not be initialized.')
                return;
            }

            this.categories.loading = true;

            const data = {
                category: { id: this.category.id },
                cms: { id: this.form.cms }
            }
            method(url, data, config)
                // eslint-disable-next-line
                .then(({ data }) => {
                    this.error = false
                    if (data.export) {
                        this.currentExport = data.export;
                    }

                    // const request = _.get(data, 'export.request');
                    // const sourceID = _.get(request, 'source.id');

                    // if (data.tree && data.tree.length) {
                    //     this.categories.items = data.tree;
                    //     const findActiveCategory = (categories) => {
                    //         categories.forEach(category => {
                    //             if (!this.form.source && (category.active || category.id === sourceID)) {
                    //                 this.$set(this.form, 'source', category)
                    //             }
                    //             if (!this.form.source && category.children && category.children.length) findActiveCategory(category.children)
                    //         })
                    //     }
                    //     findActiveCategory(data.tree)
                    // } else {
                    //     this.categories.items = [];
                    // }

                    // if (request && typeof request === 'object') {
                    //     Object.entries(request).forEach(([key, value]) => {
                    //         if (['source', 'target'].includes(key) === false) this.$set(this.form, key, value);
                    //     })
                    // }
                })
                .catch(error => {
                    // eslint-disable-next-line
                    console.error(error);
                    this.error = this.$t('Technical issue! Product export could not be initialized.')
                })
                .finally(() => {
                    this.categories.loading = false;
                })
        },
        getTreeselectValueLabel(node) {
            let label = `${node.raw.label}`;
            const addPrefix = node => {
                if (node.parentNode) {
                    label = `${node.parentNode.raw.label} <i class="el-icon-fa-angle-right fas"></i> ${label}`
                    addPrefix(node.parentNode);
                }
            }
            addPrefix(node);
            return label;
        },
        start() {
            if (!this.actions.start) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Product export could not be started.'),
                    iconClass: 'fas el-icon-fa-times-circle',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            const settings = this.settingValues;

            let api = this.actions.start.api;
            let url = this.actions.start.url;
            let token = this.actions.start.token;

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            // console.log(key, api, url, token, data);

            let method, config;
            switch (api.toLowerCase()) {
                case 'rest':
                    if (token) {
                        config = {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        };
                    }
                    method = ProductExportApi.REST;
                    break;

                // TODO
                // case 'graphql':
                //     method = MicroserviceApi.REST
                //     break;
            }

            // fetch categories
            if (!method) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Product export could not be started.'),
                    iconClass: 'fas el-icon-fa-times-circle',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            this.categories.loading = true;

            apolloClient.clearStore().then(() => {
                method(url, this.form, config)
                    .then(({ data }) => {
                        // reload categories form

                        this.currentExport = data
                        this.fetchCategories(this.activeCms.shop.id)
                    })
                    .catch(error => {
                        // eslint-disable-next-line
                        console.error(error);
                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('Product export could not be started.'),
                            iconClass: 'fas el-icon-fa-times-circle',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        })
                    })
                    .finally(() => {
                        this.categories.loading = false;
                    })
            })
        },
        stop() {
            if (!this.actions.stop) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Product export could not be stopped.'),
                    iconClass: 'fas el-icon-fa-times-circle',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            const settings = this.settingValues;

            let api = this.actions.stop.api;
            let url = this.actions.stop.url;
            let token = this.actions.stop.token;

            const prefix = 'settings:'
            if (url && url.startsWith('settings:')) {
                url = _.get(settings, url.substr(prefix.length, url.length - prefix.length))
            }
            if (token && token.startsWith('settings:')) {
                token = _.get(settings, token.substr(prefix.length, token.length - prefix.length))
            }

            // console.log(key, api, url, token, data);

            let method, config;
            switch (api.toLowerCase()) {
                case 'rest':
                    if (token) {
                        config = {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        };
                    }
                    method = ProductExportApi.REST;
                    break;

                // TODO
                // case 'graphql':
                //     method = MicroserviceApi.REST
                //     break;
            }

            // fetch categories
            if (!method) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Product export could not be stopped.'),
                    iconClass: 'fas el-icon-fa-times-circle',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return;
            }

            this.categories.loading = true;

            apolloClient.clearStore().then(() => {
                method(url, this.form, config)
                    .then(({ data }) => {
                        // reload categories form

                        this.currentExport = data
                        this.fetchCategories(this.activeCms.shop.id)
                    })
                    .catch(error => {
                        // eslint-disable-next-line
                        console.error(error);
                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            message: this.$t('Product export could not be stopped.'),
                            iconClass: 'fas el-icon-fa-times-circle',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        })
                    })
                    .finally(() => {
                        this.categories.loading = false;
                    })
            })
        },
        fetchCategories(shopId) {
            return this.categoriesStore.fetchCategories(shopId);
        }
    },
    mounted() {
        // const key = _.get(this.config, 'key');
        this.actions.initialize = _.get(this.config, 'initialize');
        this.actions.start = _.get(this.config, 'start');
        this.actions.stop = _.get(this.config, 'stop');
        this.actions.refresh = _.get(this.config, 'refresh');

        // set default field values
        const fields = _.get(this.config, 'fields') || []
        fields.forEach(field => {
            switch (field.type) {
                case 'radio':
                case 'boolean-button-group':
                    if (field.options && field.options.length) {
                        const defaultOption = field.options.find(el => el.default === true)
                        if (defaultOption) this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(defaultOption.key))
                    } else {
                        this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(field.key))
                    }
                    break;

                case 'switch':
                case 'checkbox':
                case 'boolean-button':
                    this.$set(this.form, field.key, ['true', '1', 'yes', true].includes(field.value))
                    break;

                default:
                    this.$set(this.form, field.key, field.value)

            }
        })

        this.form.cms = this.activeCms.id

        if (this.unsaved) {
            this.info = this.$t('This category has not yet been saved. You need to save it in order to export products.')
        } else {
            this.initialize()
        }
    }
}
</script>

<style lang="sass" scoped>
.product-export
    min-height: 40px
    .alert
        margin: 0
        & + .alert
            margin-top: 1rem

    .option
        padding: 0.5rem 0
        small, b
            display: block
            line-height: initial

    .inline-form-fields
        display: flex
        .el-form-item + .el-form-item
            margin-left: 0.5rem

    .export-info-panel + .dynamic-form
        margin-top: 1rem

    .dynamic-form > .el-form-item
        margin: 1rem 0

    .spacer
        flex-grow: 1

    .margin-right
        margin-right: 1rem

    .el-button-group,
    .el-form-item.buttons
        white-space: nowrap

    .el-button-group
        .el-button.is-plain
            position: relative
            // border: 1px solid
            &:active,
            &:focus
                color: $--shop-color-primary
                background: lighten($--shop-color-primary, 35%)
                border-color: $--shop-color-primary
        .el-button.is-plain.el-button--primary
            z-index: 1
            // pointer-events: none
            &:hover
                color: $--shop-color-primary
                background: lighten($--shop-color-primary, 35%)
                border-color: $--shop-color-primary
    // .percentage
        margin-bottom: 0.5rem
        .el-progress.el-progress--line
            display: flex
            align-items: center
            justify-content: space-between
            ::v-deep .el-progress__text
                text-align: right
    .running .alert
        display: flex
        align-items: center
        .percentage
            margin-right: 1rem
            margin-bottom: -1rem
            ::v-deep
                .el-progress__text
                    padding-left: 0.5rem
                path.el-progress-circle__track
                    stroke: #dad4e5 !important
</style>
